import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chat Error Boundary:', error, errorInfo);
  }

  handleReset = () => {
    // Nettoyer les données de chat
    localStorage.removeItem('adha_chat_data');
    sessionStorage.removeItem('adha_chat_data');
    
    // Réinitialiser l'état du boundary
    this.setState({ hasError: false, error: undefined });
    
    // Recharger la page pour un clean restart
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="bg-white rounded-lg shadow-xl border border-red-200 p-4 max-w-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-red-800">Erreur Chat</h4>
                <p className="text-xs text-red-600">Le chat a rencontré une erreur</p>
              </div>
            </div>
            
            <div className="bg-red-50 rounded-md p-3 mb-3">
              <p className="text-xs text-red-700 font-mono mb-2">
                {this.state.error?.message || 'Erreur inconnue'}
              </p>
              <p className="text-xs text-red-600">
                Erreur liée aux données de timestamp. Le chat sera réinitialisé.
              </p>
            </div>
            
            <button
              onClick={this.handleReset}
              className="w-full bg-red-600 text-white py-2 px-3 rounded-md text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Redémarrer le chat
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}